import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, Loader2 } from "lucide-react";
import { createEnquiry } from "@/lib/api";
import { SERVICE_OPTIONS } from "@/data/content";

const empty = { name: "", email: "", company: "", service: "", message: "" };
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const widgetIdRef = useRef(null);
  const resolveTokenRef = useRef(null);

  // Load Cloudflare Turnstile script + render invisible widget once.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;

    const renderWidget = () => {
      if (!window.turnstile || widgetIdRef.current !== null) return;
      const el = document.getElementById("vs-turnstile");
      if (!el) return;
      widgetIdRef.current = window.turnstile.render(el, {
        sitekey: TURNSTILE_SITE_KEY,
        size: "invisible",
        callback: (token) => {
          const r = resolveTokenRef.current;
          resolveTokenRef.current = null;
          if (r) r(token);
        },
        "error-callback": () => {
          const r = resolveTokenRef.current;
          resolveTokenRef.current = null;
          if (r) r(null);
        },
        "expired-callback": () => {
          if (window.turnstile && widgetIdRef.current !== null) {
            window.turnstile.reset(widgetIdRef.current);
          }
        },
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else if (!document.getElementById("cf-turnstile-api")) {
      const s = document.createElement("script");
      s.id = "cf-turnstile-api";
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      s.async = true;
      s.defer = true;
      s.onload = renderWidget;
      document.head.appendChild(s);
    } else {
      const iv = setInterval(() => {
        if (window.turnstile) {
          clearInterval(iv);
          renderWidget();
        }
      }, 120);
      return () => clearInterval(iv);
    }
    return undefined;
  }, []);

  const getTurnstileToken = () =>
    new Promise((resolve) => {
      if (!TURNSTILE_SITE_KEY || !window.turnstile || widgetIdRef.current === null) {
        // No site key configured — backend will still verify (or accept if
        // TURNSTILE_SECRET_KEY unset in dev). Never blocks the user in preview.
        resolve("");
        return;
      }
      resolveTokenRef.current = resolve;
      try {
        try {
          window.turnstile.reset(widgetIdRef.current);
        } catch {
          /* widget was idle — safe to ignore */
        }
        window.turnstile.execute(widgetIdRef.current);
      } catch {
        resolveTokenRef.current = null;
        resolve(null);
      }
      // Safety timeout in case Turnstile silently stalls
      setTimeout(() => {
        if (resolveTokenRef.current === resolve) {
          resolveTokenRef.current = null;
          resolve(null);
        }
      }, 15000);
    });

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and project details.");
      return;
    }
    setLoading(true);
    try {
      const token = await getTurnstileToken();
      if (TURNSTILE_SITE_KEY && !token) {
        toast.error("Human verification failed. Please try again.");
        setLoading(false);
        return;
      }
      await createEnquiry({ ...form, turnstile_token: token || "" });
      toast.success("Enquiry received. We'll be in touch shortly.");
      setForm(empty);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-[#0a0a0a] border-white/10 text-white h-12 rounded-lg focus-visible:ring-1 focus-visible:ring-zinc-400 focus-visible:border-zinc-500 placeholder:text-zinc-600";

  return (
    <section
      id="contact"
      className="relative overflow-hidden bg-[#050505] px-5 sm:px-8 lg:px-12 py-24 sm:py-32"
      data-testid="contact"
    >
      <div className="pointer-events-none absolute inset-0 spotlight-top opacity-70" />
      <div className="relative mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <span className="mb-5 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.3em] text-zinc-500">
            <span className="h-px w-10 bg-zinc-600" />
            Enquire
          </span>
          <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight leading-[0.95] text-chrome">
            Let&apos;s build something unforgettable
          </h2>
          <p className="mt-8 max-w-md text-base font-light leading-relaxed text-zinc-400">
            Tell us about your show, tour or venue. We reply to every serious
            enquiry within two working days.
          </p>
          <div className="mt-10 space-y-4 text-sm text-zinc-500">
            <div>
              <span className="text-zinc-600 uppercase tracking-widest text-[10px]">Email</span>
              <div className="text-zinc-300">
                <a href="mailto:hello@thevisionstudio.in" className="hover:text-white transition-colors">hello@thevisionstudio.in</a>
              </div>
            </div>
            <div>
              <span className="text-zinc-600 uppercase tracking-widest text-[10px]">Phone</span>
              <div className="text-zinc-300 space-y-1">
                <div>
                  <a href="tel:+919833832211" className="hover:text-white transition-colors">+91 98338 32211</a>
                </div>
                <div>
                  <a href="tel:+919699701515" className="hover:text-white transition-colors">+91 96997 01515</a>
                </div>
              </div>
            </div>
            <div>
              <span className="text-zinc-600 uppercase tracking-widest text-[10px]">Studio</span>
              <div className="text-zinc-300 leading-relaxed">
                408, Mohid Heights, Suresh Nagar,<br />
                RTO Lane, Andheri West,<br />
                Mumbai — 400053
              </div>
            </div>
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7 rounded-2xl border border-white/10 bg-black/40 backdrop-blur-xl p-7 sm:p-10 space-y-6"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Name</Label>
              <Input
                data-testid="contact-name"
                value={form.name}
                onChange={set("name")}
                placeholder="Your name"
                className={inputCls}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Email</Label>
              <Input
                data-testid="contact-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="you@company.com"
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Company</Label>
              <Input
                data-testid="contact-company"
                value={form.company}
                onChange={set("company")}
                placeholder="Optional"
                className={inputCls}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Service</Label>
              <Select value={form.service} onValueChange={set("service")}>
                <SelectTrigger
                  data-testid="contact-service"
                  className={inputCls + " data-[placeholder]:text-zinc-600"}
                >
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10 text-zinc-200">
                  {SERVICE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s} className="focus:bg-white/10 focus:text-white">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">
              Project details
            </Label>
            <Textarea
              data-testid="contact-message"
              value={form.message}
              onChange={set("message")}
              rows={5}
              placeholder="Tell us about the show, dates, venue and scope..."
              className="bg-[#0a0a0a] border-white/10 text-white rounded-lg focus-visible:ring-1 focus-visible:ring-zinc-400 focus-visible:border-zinc-500 placeholder:text-zinc-600 resize-none"
            />
          </div>

          <div
            id="vs-turnstile"
            data-testid="turnstile-widget"
            style={{ height: 0, overflow: "hidden" }}
          />

          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="group inline-flex items-center gap-3 rounded-full bg-white px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-black transition-all duration-300 hover:bg-zinc-200 disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Sending
              </>
            ) : (
              <>
                Send Enquiry
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
